import { styled } from "@mui/material";
import { ChevronLeft } from "lucide-react";
import { tokens } from "@styles/tokens";

interface DrawerHeaderProps {
  title?: string;
  subtitle?: string;
  onClose: () => void;
}

/**
 * Shared drawer header for the forms pattern: a back chevron that dismisses the
 * drawer, the form title, and an optional subtitle. Used by the app-wide
 * DrawerHost and the stock form's nested create drawers so every form's chrome
 * is identical.
 */
export const DrawerHeader = ({ title, subtitle, onClose }: DrawerHeaderProps) => (
  <Header>
    <BackButton type="button" onClick={onClose} aria-label="Close">
      <ChevronLeft size={22} />
    </BackButton>
    <TitleBlock>
      <Title>{title}</Title>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
    </TitleBlock>
  </Header>
);

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: tokens.space(2),
  padding: tokens.space(3),
  flexShrink: 0,
  borderBottom: `1px solid ${theme.skuey.border}`,
  backgroundColor: theme.skuey.surface,
}));

const BackButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  flexShrink: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  borderRadius: tokens.radius.sm,
  color: theme.skuey.body,
  "&:hover": { backgroundColor: theme.skuey.surfaceAlt },
}));

const TitleBlock = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
});

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.h3[0],
  fontWeight: tokens.font.weight.bold,
  color: theme.skuey.ink,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Subtitle = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  color: theme.skuey.muted,
}));
